import React from 'react';
import { FileText, Target, CheckCircle2, X } from 'lucide-react';

export function PracticeProtocol({ t }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="bg-[#002B5B] p-3 sm:p-4 text-white flex items-center gap-2 sm:gap-3">
        <FileText size={20} />
        <h2 className="text-sm sm:text-lg font-bold uppercase tracking-tight">{t.practice.protocolTitle}</h2>
      </div>

      <div className="bg-[#FFD700] p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
        <Target className="text-gray-900 shrink-0" size={18} />
        <p className="text-gray-900 text-xs sm:text-sm"><span className="font-bold">{t.practice.objective}:</span> {t.practice.objectiveDesc}</p>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        <section className="space-y-3">
          <div className="bg-blue-50 p-2.5 sm:p-3 rounded-lg flex items-center gap-2 border-l-4 border-blue-500">
            <span className="bg-blue-500 text-white w-6 h-6 rounded flex items-center justify-center font-bold text-sm">1</span>
            <h3 className="font-bold text-blue-900 text-sm uppercase">{t.practice.mandatoryRegistration}</h3>
          </div>
          <div className="pl-4 sm:pl-6 text-sm text-gray-700 space-y-2">
            <p>{t.practice.markDayWith}:</p>
            <div className="space-y-1">
              <p><span className="font-bold text-emerald-600">VERDE</span> = {t.practice.correctDecision}</p>
              <p><span className="font-bold text-red-600">ROJO</span> = {t.practice.ruleViolation}</p>
            </div>
            <p className="text-gray-500 italic text-xs">{t.practice.noBlankSpaces}</p>
          </div>
        </section>

        <section className="space-y-3">
          <div className="bg-emerald-50 p-2.5 sm:p-3 rounded-lg flex items-center gap-2 border-l-4 border-emerald-500">
            <span className="bg-emerald-500 text-white w-6 h-6 rounded flex items-center justify-center font-bold text-sm">2</span>
            <h3 className="font-bold text-emerald-900 text-sm uppercase">{t.practice.consideredOneWhen}</h3>
          </div>
          <ul className="pl-4 sm:pl-6 text-sm text-gray-700 space-y-2">
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" /> {t.practice.onlyTradedStrategy}</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" /> {t.practice.noTradeNoSignal}</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" /> {t.practice.respectedRiskPlan}</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" /> {t.practice.executedNoImprov}</li>
          </ul>
          <p className="pl-4 sm:pl-6 font-black text-emerald-800 text-sm uppercase tracking-wide">{t.practice.disciplineMatters}</p>
        </section>

        <section className="space-y-3">
          <div className="bg-amber-50 p-2.5 sm:p-3 rounded-lg flex items-center gap-2 border-l-4 border-amber-500">
            <span className="bg-amber-500 text-white w-6 h-6 rounded flex items-center justify-center font-bold text-sm">3</span>
            <h3 className="font-bold text-amber-900 text-sm uppercase">{t.practice.consideredZeroWhen}</h3>
          </div>
          <ul className="pl-4 sm:pl-6 text-sm text-gray-700 space-y-2">
            <li className="flex items-start gap-2"><X size={16} className="text-red-500 mt-0.5 shrink-0" /> {t.practice.forcedEntry}</li>
            <li className="flex items-start gap-2"><X size={16} className="text-red-500 mt-0.5 shrink-0" /> {t.practice.noConfirmation}</li>
            <li className="flex items-start gap-2"><X size={16} className="text-red-500 mt-0.5 shrink-0" /> {t.practice.brokeRiskManagement}</li>
            <li className="flex items-start gap-2"><X size={16} className="text-red-500 mt-0.5 shrink-0" /> {t.practice.actedImpulse}</li>
          </ul>
        </section>

        <section className="space-y-3">
          <div className="bg-blue-50 p-2.5 sm:p-3 rounded-lg flex items-center gap-2 border-l-4 border-blue-400">
            <span className="bg-blue-400 text-white w-6 h-6 rounded flex items-center justify-center font-bold text-sm">4</span>
            <h3 className="font-bold text-blue-900 text-sm uppercase">{t.practice.approvalCriteria}</h3>
          </div>
          <div className="pl-4 sm:pl-6 text-sm text-gray-700 space-y-2">
            <p className="font-bold text-gray-900 uppercase">{t.practice.toOperateReal}</p>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0"></span>
                {t.practice.minDisciplineLastMonth}
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0"></span>
                {t.practice.noConsecutiveErrors}
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0"></span>
                {t.practice.clearUnderstanding}
              </li>
            </ul>
          </div>
        </section>

        <section className="space-y-3">
          <div className="bg-gray-100 p-2.5 sm:p-3 rounded-lg flex items-center gap-2 border-l-4 border-gray-400">
            <span className="bg-gray-500 text-white w-6 h-6 rounded flex items-center justify-center font-bold text-sm">5</span>
            <h3 className="font-bold text-gray-800 text-sm uppercase">{t.practice.ifPercentLow}</h3>
          </div>
          <div className="pl-4 sm:pl-6 text-sm">
            <p className="font-black text-red-600 text-base">{t.practice.noRealOperate}</p>
            <p className="text-gray-600">{t.practice.continuePractice}</p>
          </div>
        </section>

        <section className="space-y-3">
          <div className="bg-pink-50 p-2.5 sm:p-3 rounded-lg flex items-center gap-2 border-l-4 border-pink-400">
            <span className="bg-pink-400 text-white w-6 h-6 rounded flex items-center justify-center font-bold text-sm">6</span>
            <h3 className="font-bold text-pink-900 text-sm uppercase">{t.practice.fundamentalPrinciple}</h3>
          </div>
          <div className="pl-4 sm:pl-6 text-sm text-gray-700 space-y-1">
            <p>{t.practice.protectCapital}</p>
            <p>{t.practice.noTradeIsCorrect}</p>
          </div>
        </section>
      </div>
    </div>
  );
}
